<template>
  <div class="bg-background-neutral-secondary rounded-3xl p-4">
    <div v-if="special" class="description-content">
      <div
        v-if="isExpanded"
        @click="handleDescriptionClick"
        v-html="fullDescriptionWithButton"
      ></div>
      <div
        v-else
        @click="handleDescriptionClick"
        v-html="truncatedDescriptionWithButton"
      ></div>
    </div>

    <div class="mt-4 space-y-4">
      <!-- Event-specific fields -->
      <template v-if="isEvent">
        <div v-if="special.date" class="flex items-center gap-2">
          <div class="bg-background-positive-invisible rounded-xl p-2">
            <CalendarIcon />
          </div>
          <div class="font-medium">
            <p class="text-text-neutral-quaternary text-xs">
              {{ $t('specialOffer.details.eventDate') }}:
            </p>
            <p class="text-text-neutral-primary text-sm">
              {{ formatDate(special.date) }}
            </p>
          </div>
        </div>

        <div v-if="special.location" class="flex items-center gap-2">
          <div class="bg-background-positive-invisible rounded-xl p-2">
            <LocationIcon />
          </div>
          <div class="font-medium">
            <p class="text-text-neutral-quaternary text-xs">
              {{ $t('specialOffer.details.eventLocation') }}:
            </p>
            <p class="text-text-neutral-primary text-sm">
              {{ special.location.city.name }},
              {{ special.location.country.name }}
            </p>
          </div>
        </div>
      </template>

      <!-- Airline promo-specific fields -->
      <template v-else>
        <div v-if="special.purchase_period" class="flex items-center gap-2">
          <div class="bg-background-positive-invisible rounded-xl p-2">
            <CalendarIcon />
          </div>
          <div class="font-medium">
            <p class="text-text-neutral-quaternary text-xs">
              {{ $t('specialOffer.details.purchasePeriod') }}:
            </p>
            <p class="text-text-neutral-primary text-sm">
              {{ formatDate(special.purchase_period.from) }} -
              {{ formatDate(special.purchase_period.to) }}
            </p>
          </div>
        </div>

        <div v-if="special.travel_period" class="flex items-center gap-2">
          <div class="bg-background-positive-invisible rounded-xl p-2">
            <PlaneIcon />
          </div>
          <div class="font-medium">
            <p class="text-text-neutral-quaternary text-xs">
              {{ $t('specialOffer.details.travelPeriod') }}:
            </p>
            <p class="text-text-neutral-primary text-sm">
              {{ formatDate(special.travel_period.from) }} -
              {{ formatDate(special.travel_period.to) }}
            </p>
          </div>
        </div>

        <DirectionsDisplay
          v-if="special.directions"
          :special="special"
          :title="$t('specialOffer.details.destinations')"
        />
      </template>
    </div>

    <FreeButton
      v-if="isEvent && special.event_tickets_url"
      class="gray-button mt-6 w-full text-base"
      type="button"
      size="sm"
      @click="$emit('onTicketLinkClick')"
    >
      {{ $t('specialOffer.details.concertTickets') }}
    </FreeButton>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { FreeButton } from 'freedom-shared';
  import type { specialOffers } from 'shared';
  import dayjs from 'dayjs';

  import CalendarIcon from '@/assets/images/green-calendar-icon.svg?component';
  import PlaneIcon from '@/assets/images/green-plane-icon.svg?component';
  import LocationIcon from '@/assets/images/green-location-icon.svg?component';

  import DirectionsDisplay from './DirectionsDisplay.vue';
  import { useTruncatedHtml } from '../model';

  const props = defineProps<{
    special: specialOffers.Special;
  }>();

  defineEmits<{
    onTicketLinkClick: [];
  }>();

  const isEvent = computed(() => props.special?.type === 'EVENT');

  const {
    isExpanded,
    truncatedDescriptionWithButton,
    fullDescriptionWithButton,
    handleDescriptionClick,
  } = useTruncatedHtml(() => props.special?.description, 220);

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format('D MMMM YYYY');
  };
</script>

<style scoped>
  .description-content :deep(p:first-child) {
    @apply text-text-neutral-primary text-lg font-semibold;
  }

  .description-content :deep(p:not(:first-child)) {
    @apply text-text-neutral-primary text-base;
  }
</style>
